import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Textarea } from './ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2, 
  Package, 
  Truck, 
  Calendar,
  FileText,
  User,
  Building
} from 'lucide-react';
import { grnApiService, GRN, GRNDetail } from '../services/grnApi';
import { purchaseOrderApiService } from '../services/purchaseOrderApi';
import { storeApiService, Store } from '../services/storeApi';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';

// Utility function to format date to yyyy-MM-dd HH:mm:ss
const formatDateForAPI = (date: Date | string): string => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};



interface GRNItem {
  id: string;
  lineNumber: number;
  quantity: number;
  receivedQuantity: number;
  unitPrice: number;
  lineTotal: number;
  qualityCheck: string;
  remarks: string;
  heatNo: string;
  tcNo: string;
  tcDate: string;
  supplierHeatNo: string;
  nop?: number;
  rawItemId: number;
  itemName: string;
  itemCode: string;
  unit: string;
  remainingQuantity: number; // Track remaining quantity from PO
  storeId: number | null; // Store where this item will be allocated
}

interface GRNFormData {
  grnNumber: string;
  grnDate: string;
  receiveDate: string;
  poHeaderId: number | null;
  poNumber: string;
  status: string;
  receivedBy: string;
  vehicleNumber: string;
  driverName: string;
  totalQuantity: number;
  totalAmount: number;
  notes: string;
  type: 'MANUAL' | 'PO';
  isActive: boolean;
  branchId: number;
  dcNo: string;
  dcDate: string;
  createdBy: number;
  updatedBy: number;
  items: GRNItem[];
}

const GRNForm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  // GRNForm is ONLY used for create mode
  const isCreateMode = true;
  const isViewMode = false;
  const isEditMode = false;

  const [formData, setFormData] = useState<GRNFormData>({
    grnNumber: '',
    grnDate: formatDateForAPI(new Date()),
    receiveDate: formatDateForAPI(new Date()),
    poHeaderId: null,
    poNumber: '',
    status: 'RECEIVED',
    receivedBy: '',
    vehicleNumber: '',
    driverName: '',
    totalQuantity: 0,
    totalAmount: 0,
    notes: '',
    type: 'PO',
    isActive: true,
    branchId: selectedBranch || 0,
    dcNo: '',
    dcDate: formatDateForAPI(new Date()),
    createdBy: 1,
    updatedBy: 1,
    items: []
  });

  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([]);
  // Store raw string values for received quantity inputs to allow decimal input
  const [receivedQtyInputs, setReceivedQtyInputs] = useState<Record<string, string>>({});
  const [selectedPO, setSelectedPO] = useState<any>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(false);
  const [poOpen, setPoOpen] = useState(false);
  const [poSearchValue, setPoSearchValue] = useState('');

  const getRemainingQuantityDisplay = (item: GRNItem) => {
    const receivedQty = item.receivedQuantity ?? 0;
    if (isCreateMode) {
      if (item.remainingQuantity === undefined || item.remainingQuantity === null) {
        const poQuantity = item.quantity ?? 0;
        return Math.max(poQuantity - receivedQty, 0);
      }
      return Math.max(item.remainingQuantity - receivedQty, 0);
    }
    const poQuantity = item.quantity ?? 0;
    return Math.max(poQuantity - receivedQty, 0);
  };

  // Filter purchase orders based on search
  const filteredPurchaseOrders = purchaseOrders.filter(po => 
    po && po.poNumber && po.poNumber.toLowerCase().includes(poSearchValue.toLowerCase())
  );
  
  // console.log('Purchase Orders:', purchaseOrders);
  // console.log('Filtered Purchase Orders:', filteredPurchaseOrders);
  // console.log('Search Value:', poSearchValue);

  // Load purchase orders for the selected branch
  useEffect(() => {
    const loadPurchaseOrders = async () => {
      try {
        const branchId = selectedBranch;
        if (!branchId) {
          // console.warn('Selected branch information not available');
          setPurchaseOrders([]);
          return;
        }
        
        const response = await purchaseOrderApiService.getApprovedPONumbersByBranch(branchId);
        // console.log('PO API Response:', response);
        
        // The API service returns PoHeader[] but the actual response has the format we need
        if (Array.isArray(response) && response.length > 0) {
          // Transform the array of PO objects into the format we need
          const pos = response.map((po: any) => ({
            id: po.id,
            poNumber: po.poNumber
          }));
          // console.log('Transformed POs:', pos);
          setPurchaseOrders(pos);
        } else {
          // console.log('No POs found or invalid response format');
          setPurchaseOrders([]);
        }
      } catch (error) {
        // console.error('Error loading purchase orders:', error);
        toast.error('Failed to load purchase orders');
        setPurchaseOrders([]);
      }
    };

    const loadStores = async () => {
      try {
        const branchId = selectedBranch;
        if (!branchId) {
          // console.warn('Selected branch information not available');
          setStores([]);
          return;
        }
        
        // console.log('Loading stores for branchId:', branchId);
        const response = await storeApiService.getStoresByBranch(branchId);
        // console.log('Stores API Response:', response);
        setStores(response);
      } catch (error) {
        // console.error('Error loading stores:', error);
        toast.error('Failed to load stores');
        setStores([]);
      }
    };
    
    if (selectedBranch) {
      loadPurchaseOrders();
      loadStores();
      // Update branchId in formData when selectedBranch changes (only in create mode)
      if (isCreateMode) {
        setFormData(prev => ({
          ...prev,
          branchId: selectedBranch || 0
        }));
      }
    }
  }, [selectedBranch, isCreateMode]);

  // GRNForm is only for create mode - no need to load existing data



  const handlePOSelect = async (poId: string) => {
    const po = purchaseOrders.find((po: any) => po && po.id.toString() === poId);
    if (po) {
      setSelectedPO(po);
      setPoOpen(false);
      
      try {
        // Fetch full PO details using PO ID
        const poDetails = await purchaseOrderApiService.getPurchaseOrderById(po.id.toString());
        // console.log('PO Details loaded:', poDetails);
        
        // Update selectedPO with full details
        setSelectedPO(poDetails);
        
        // Fetch PO items using PO ID
        const poItems = await purchaseOrderApiService.getPoItemDetails(po.id.toString());
        
        // console.log('PO Items loaded for PO ID:', po.id, poItems);
        // console.log('First PO Item:', poItems[0]);
        
        // Check if poItems is valid array
        if (!Array.isArray(poItems)) {
          // console.error('PO Items is not an array:', poItems);
          toast.error('Invalid PO items data received');
          return;
        }
        
        // Initialize DC date to today's date
        const todayDate = new Date().toISOString().split('T')[0];
        
        setFormData(prev => ({
          ...prev,
          poHeaderId: poDetails.id, // Use the full PO details ID
          poNumber: poDetails.poNumber,
          dcDate: todayDate + ' 00:00:00',
          items: poItems.map((item: any, index: number) => {
            // console.log(`Mapping PO Item ${index}:`, item);
            // console.log(`Item ${index} rawItem:`, item.rawItem);
            // console.log(`Item ${index} rawItemId:`, item.rawItem?.id);
            
            return {
              id: Date.now().toString() + Math.random(),
              lineNumber: index + 1,
              quantity: item.quantity || 0,
              receivedQuantity: 0,
              unitPrice: item.unitPrice || 0,
              lineTotal: 0,
              qualityCheck: '',
              remarks: '',
              heatNo: '', // Will be generated on server side
              tcNo: '',
              tcDate: '',
              supplierHeatNo: '',
              nop: undefined,
              rawItemId: item.rawItem?.id || item.rawItemId || 0,
              itemName: item.rawItem?.itemName || item.itemDescription || 'Unknown Item',
              itemCode: item.rawItem?.sku || '',
              unit: item.unitOfMeasure || '',
            remainingQuantity: Math.max((item.quantity || 0) - (item.receivedQuantity || 0), 0),
              storeId: null // Will be selected by user for each item
            };
          })
        }));
      } catch (error) {
        // console.error('Error loading PO details or items:', error);
        toast.error('Failed to load purchase order details');
      }
    }
  };


  const updateItem = (itemId: string, field: keyof GRNItem, value: string | number | undefined) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          
            // Recalculate line total if received quantity changes
            if (field === 'receivedQuantity') {
              const receivedQty = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
              
              // Calculate remaining quantity from PO (create mode only)
              const actualRemainingQuantity = item.remainingQuantity;
              
              // Check if item is fully received (remaining qty is 0 or less)
              if (actualRemainingQuantity <= 0 && receivedQty > 0) {
                toast.error('This item is fully received. Cannot receive more.');
                return item; // Return unchanged item
              }
              
              // Validate that received quantity doesn't exceed the actual remaining quantity
              if (receivedQty > actualRemainingQuantity) {
                toast.error(`Received quantity cannot exceed remaining quantity (${actualRemainingQuantity})`);
                return item; // Return unchanged item
              }
              
              updatedItem.lineTotal = receivedQty * updatedItem.unitPrice;
            }
          
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const removeItem = (itemId: string) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const handleSave = async () => {
    if (!formData.poHeaderId) {
      toast.error('Please select a Purchase Order');
      return;
    }

    if (!formData.dcNo || formData.dcNo.trim() === '') {
      toast.error('DC Number is required. Please enter DC Number before creating GRN.');
      return;
    }

    if (formData.items.length === 0) {
      toast.error('No items to receive');
      return;
    }

    const hasReceivedItems = formData.items.some(item => item.receivedQuantity > 0);
    if (!hasReceivedItems) {
      toast.error('Please enter received quantities for at least one item');
      return;
    }

    try {
      setLoading(true);
      
      // GRNForm is only for create mode
      const grnData = {
        grnDate: formatDateForAPI(formData.grnDate),
        receiveDate: formatDateForAPI(formData.receiveDate),
        poHeaderId: formData.poHeaderId,
        supplierId: selectedPO?.supplier?.id || 0,
        status: formData.status,
        receivedBy: formData.receivedBy,
        vehicleNumber: formData.vehicleNumber,
        driverName: formData.driverName,
        notes: formData.notes,
        type: formData.type,
        isActive: formData.isActive,
        branchId: selectedBranch || 0,
        storeId: 0, // Default store ID since we're using per-item store selection
        dcNo: formData.dcNo || '',
        dcDate: formData.dcDate ? formatDateForAPI(formData.dcDate) : '',
        createdBy: formData.createdBy,
        updatedBy: formData.updatedBy
      };
      
      // console.log('GRN Data being sent to API:', grnData);
      
      const createdGRN = await grnApiService.createGRN(grnData);
      // console.log('GRN created, API response:', createdGRN);
      const createdGRNId = createdGRN.id;
      toast.success('GRN created successfully');

      // Save GRN details
      for (const item of formData.items) {
        if (item.receivedQuantity > 0) {
          // Base data without TC fields
          const baseGrnDetailData = {
            lineNumber: item.lineNumber,
            quantity: item.quantity,
            receivedQuantity: item.receivedQuantity,
            unitPrice: item.unitPrice,
            lineTotal: item.lineTotal,
            qualityCheck: item.qualityCheck,
            remarks: item.remarks,
            heatNo: item.heatNo,
            supplierHeatNo: item.supplierHeatNo,
            nop: item.nop,
            branchId: selectedBranch || 0,
            storeId: item.storeId || null, // Set to null if store is not selected
            grnId: createdGRNId,
            rawItemId: item.rawItemId,
            createdBy: formData.createdBy,
            updatedBy: formData.updatedBy
          };

          // TC fields are never included - they're only managed in Access Documents page
          const grnDetailData = baseGrnDetailData;
          
          // console.log('GRN Detail Data being sent:', grnDetailData);
          // console.log('Item rawItemId:', item.rawItemId);

          await grnApiService.createGRNDetail(grnDetailData);
        }
      }

      navigate('/grn');
    } catch (error) {
      // Error toast is handled inside grnApiService (createGRN / createGRNDetail)
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/purchases/grn');
  };

  // Check if Save button should be enabled
  const isSaveEnabled = () => {
    // Must have a PO selected
    if (!formData.poHeaderId) {
      return false;
    }
    
    // Must have at least one item with received quantity > 0
    const hasReceivedItems = formData.items.some(item => item.receivedQuantity > 0);
    if (!hasReceivedItems) {
      return false;
    }
    
    return true;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="w-full space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/grn')}
              className="border-primary-200 text-primary-700 hover:bg-primary-50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to GRN List
            </Button>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
                Create GRN
              </h1>
              <p className="text-gray-600 mt-1">
                Create a new Goods Received Note
              </p>
            </div>
          </div>
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* GRN Details */}
          <Card className="border-primary-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary-700" />
                GRN Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="receiveDate">Received Date</Label>
                  <Input
                    id="receiveDate"
                    type="date"
                    value={formData.receiveDate.split(' ')[0]}
                    onChange={(e) => setFormData(prev => ({ ...prev, receiveDate: e.target.value + ' 00:00:00' }))}
                    max={new Date().toISOString().split('T')[0]}
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white"
                  />
                </div>
                <div>
                  <Label htmlFor="poHeaderId">Purchase Order</Label>
                  <Popover open={poOpen} onOpenChange={setPoOpen}>
                    <PopoverTrigger className="w-full">
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={poOpen}
                        className="w-full justify-between border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white"
                      >
                        {selectedPO ? selectedPO.poNumber : "Select Purchase Order..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0 z-[9999] bg-white border shadow-lg">
                      <div className="p-2">
                        <Input
                          placeholder="Search purchase orders..."
                          value={poSearchValue}
                          onChange={(e) => setPoSearchValue(e.target.value)}
                          className="mb-2"
                        />
                        <div className="max-h-60 overflow-auto">
                          {filteredPurchaseOrders.length === 0 ? (
                            <div className="p-2 text-sm text-gray-500">No purchase order found.</div>
                          ) : (
                            filteredPurchaseOrders.map((po) => (
                              <div
                                key={po.id}
                                className="flex items-center p-2 hover:bg-gray-100 cursor-pointer rounded"
                                onClick={() => handlePOSelect(po.id.toString())}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    selectedPO?.id === po.id ? "opacity-100" : "opacity-0"
                                  }`}
                                />
                                <div className="flex flex-col">
                                  <span className="font-medium">{po.poNumber}</span>
                                  {/* <span className="text-sm text-gray-500">{po.supplier?.supplierName || 'N/A'}</span> */}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delivery Information */}
          <Card className="border-primary-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary-700" />
                Delivery Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="dcNo">DC Number</Label>
                  <Input
                    id="dcNo"
                    value={formData.dcNo || ''}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        dcNo: e.target.value
                      }));
                    }}
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white"
                    placeholder="Enter DC number"
                  />
                </div>
                <div>
                  <Label htmlFor="dcDate">DC Date</Label>
                  <Input
                    id="dcDate"
                    type="date"
                    value={formData.dcDate ? formData.dcDate.split(' ')[0] : ''}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        dcDate: selectedDate + ' 00:00:00'
                      }));
                    }}
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white"
                  />
                </div>
                <div>
                  <Label htmlFor="vehicleNumber">Vehicle Number</Label>
                  <Input
                    id="vehicleNumber"
                    value={formData.vehicleNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, vehicleNumber: e.target.value }))}
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white"
                  />
                </div>
                <div>
                  <Label htmlFor="driverName">Driver Name</Label>
                  <Input
                    id="driverName"
                    value={formData.driverName}
                    onChange={(e) => setFormData(prev => ({ ...prev, driverName: e.target.value }))}
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white"
                  />
                </div>
                <div>
                  <Label htmlFor="receivedBy">Received By</Label>
                  <Input
                    id="receivedBy"
                    value={formData.receivedBy}
                    onChange={(e) => setFormData(prev => ({ ...prev, receivedBy: e.target.value }))}
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* PO Summary */}
          {selectedPO && (
            <Card className="border-primary-200 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary-700" />
                  PO Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-gray-600">PO Number</Label>
                  <p className="font-medium">{selectedPO.poNumber || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Supplier</Label>
                  <p className="font-medium">{selectedPO.supplier?.supplierName || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Order Date</Label>
                  <p className="font-medium">{selectedPO.orderDate || selectedPO.purchaseDate || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Expected Delivery</Label>
                  <p className="font-medium">{selectedPO.expectedDeliveryDate || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Status</Label>
                  <Badge variant="outline" className="border-green-200 text-green-700">
                    {selectedPO.status || 'N/A'}
                  </Badge>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-600">Total Amount</Label>
                  <p className="font-medium text-lg">₹{(selectedPO.totalAmount || 0).toLocaleString()}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Received Items - Full Width */}
      <Card className="border-primary-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary-700" />
            Received Items
          </CardTitle>
        </CardHeader>
        <CardContent>

          {formData.items.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Remaining Qty</TableHead>
                    <TableHead>Received Qty</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Unit Price</TableHead>
                    <TableHead>Line Total</TableHead>
                    <TableHead>Store</TableHead>
                    <TableHead>Supplier Heat No</TableHead>
                    <TableHead>NOP</TableHead>
                    <TableHead>TC No</TableHead>
                    <TableHead>TC Date</TableHead>
                    <TableHead>Remarks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{item.itemName || 'Unknown Item'}</div>
                          <div className="text-sm text-gray-500">{item.itemCode || '-'}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{item.quantity || 0}</span>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-blue-600">
                          {item.remainingQuantity?.toFixed(2) ?? item.quantity?.toFixed(2) ?? '0.00'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {
                          (() => {
                            const remainingQty = item.remainingQuantity ?? item.quantity ?? 0;
                            const isFullyReceived = remainingQty <= 0;
                            
                            return (
                              <Input
                                type="text"
                                value={receivedQtyInputs[item.id] ?? (item.receivedQuantity?.toString() || '')}
                                onChange={(e) => {
                                  // Prevent input if item is fully received
                                  if (isFullyReceived) {
                                    toast.error('This item is fully received. Cannot receive more.');
                                    return;
                                  }
                                  
                                  let value = e.target.value;
                                  const currentValue = receivedQtyInputs[item.id] ?? (item.receivedQuantity?.toString() || '');
                                  
                                  // Replace "0" with new digit when user starts typing
                                  if (currentValue === '0' && value.length === 1 && /^[1-9]$/.test(value)) {
                                    value = value; // Already correct, just for clarity
                                  } else if (/^0+[1-9]/.test(value)) {
                                    value = value.replace(/^0+/, ''); // Remove leading zeros
                                  }
                                  
                                  // Only allow valid numeric/decimal input
                                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                    setReceivedQtyInputs(prev => ({ ...prev, [item.id]: value }));
                                    
                                    // Update numeric value only if complete (not ending with ".")
                                    if (value && !value.endsWith('.')) {
                                      const numValue = parseFloat(value);
                                      if (!isNaN(numValue)) {
                                        updateItem(item.id, 'receivedQuantity', numValue);
                                      }
                                    } else if (value === '' || value === '.') {
                                      updateItem(item.id, 'receivedQuantity', 0);
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  if (isFullyReceived) {
                                    return;
                                  }
                                  
                                  const value = e.target.value.trim() || '0';
                                  const numValue = parseFloat(value) || 0;
                                  updateItem(item.id, 'receivedQuantity', numValue);
                                  setReceivedQtyInputs(prev => {
                                    const { [item.id]: _, ...rest } = prev;
                                    return rest;
                                  });
                                }}
                                disabled={isFullyReceived}
                                className={`w-20 border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white ${
                                  isFullyReceived ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                                }`}
                                placeholder="0.00"
                                title={isFullyReceived ? 'Item is fully received' : ''}
                              />
                            );
                          })()
                        }
                      </TableCell>
                      <TableCell>{item.unit || '-'}</TableCell>
                      <TableCell>₹{item.unitPrice || 0}</TableCell>
                      <TableCell>₹{item.lineTotal || 0}</TableCell>
                      <TableCell>
                        {
                          (() => {
                            const remainingQty = item.remainingQuantity ?? item.quantity ?? 0;
                            const isFullyReceived = remainingQty <= 0;
                            
                            return (
                              <Select
                                value={item.storeId?.toString() || ''}
                                onValueChange={(value) => updateItem(item.id, 'storeId', parseInt(value))}
                                disabled={isFullyReceived}
                              >
                                <SelectTrigger className={`w-40 border-primary-200 focus:border-primary-400 focus:ring-primary-400 ${
                                  isFullyReceived ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                                }`}>
                                  <SelectValue placeholder="Select store" />
                                </SelectTrigger>
                                <SelectContent>
                                  {stores.map((store) => (
                                    <SelectItem key={store.id} value={store.id.toString()}>
                                      {store.storeName}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            );
                          })()
                        }
                      </TableCell>
                      <TableCell>
                        <Input
                          value={item.supplierHeatNo || ''}
                          onChange={(e) => updateItem(item.id, 'supplierHeatNo', e.target.value)}
                          placeholder="Enter supplier heat number"
                          className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white w-40"
                        />
                      </TableCell>
                      <TableCell>
                        {isViewMode ? (
                          <span className="text-sm">{item.nop || '-'}</span>
                        ) : (
                          <Input
                            type="number"
                            value={item.nop || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              updateItem(item.id, 'nop', value ? parseInt(value) : undefined);
                            }}
                            placeholder="NOP"
                            className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white w-24"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {isViewMode ? (
                          <span className="text-sm">{item.tcNo || '-'}</span>
                        ) : (
                          <Input
                            value={item.tcNo}
                            onChange={(e) => updateItem(item.id, 'tcNo', e.target.value)}
                            placeholder="TC No"
                            className="border-primary-200 focus:border-primary-400 focus:ring-primary-400 bg-gradient-to-r from-primary-50/30 to-white w-24"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-lg font-medium mb-2">No Items</p>
              <p className="text-sm">Select a Purchase Order to load items</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4">
        {(
          <Button 
            variant="outline" 
            onClick={handleCancel}
            className="border-primary-200 text-primary-700 hover:bg-primary-50 px-8 py-2"
          >
            Cancel
          </Button>
        )}
        {(
          <Button 
            onClick={handleSave}
            disabled={loading || !isSaveEnabled()}
            className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg shadow-primary-200 disabled:opacity-50 px-8 py-2"
          >
            {loading ? 'Saving...' : 'Save GRN'}
          </Button>
        )}
      </div>
      </div>
    </div>
  );
};

export default GRNForm;
